"use client";
import { useState, useEffect } from "react";
import Button from '@/components/button';
import styles from './_dose-reduction.module.scss';
import CustomImage from '@/components/image';

export default function HcpClinicalInformationDoseReduction({ index = 0 }) {
  const [screenSize, setScreenSize] = useState(null);
  const questionHighlight = (target) => {
    const parent = document.getElementById('DoseReduction');
    if (target == parent.dataset.questionHighlight) {
      parent.dataset.questionHighlight = "none";
    }else{
      parent.dataset.questionHighlight = target;
    }
  }

  const openLabelTreamentTooltipData = index === 0
  ? <>
      <ul className="mb-2">
        <li>
          For patients who switched from placebo to CRENESSITY, GC doses were not changed for the first 4 weeks (weeks 28-32)
        </li>
        <li>
          For patients taking &ge;11 mg/m<sup>2</sup>/day, GC doses were reduced beginning at week 32 only when androstenedione levels were maintained or improved
          <ul>
            <li>
              GC doses were reduced on an approximately monthly basis to reach a more physiologic range (8-10 mg/m<sup>2</sup>/day)
            </li>
          </ul>
        </li>
      </ul>
      <div className="tooltip__heading mb-2">
        Open-label extension<sup>6,11</sup>
      </div>
      <ul>
        <li>
          From year 1 onwards, patients could have their GC doses adjusted as tolerated to achieve the lowest GC dose that maintained adequate disease control (in the opinion of the investigator)
        </li>
        <li>
          84 patients continued to participate in the open-label extension through year 2
        </li>
      </ul>
    </>
  : <>
      <ul>
        <li>
          For patients who switched from placebo to CRENESSITY, GC doses were not changed for the first 4 weeks (weeks 24-28)
        </li>
        <li>
          For patients taking &gt;20 mg/day, GC doses were reduced only when androstenedione levels were maintained or improved 
          <ul>
            <li>
              GC doses were reduced on an approximately monthly basis to reach a more physiologic range (15-18 mg/day)<sup>c</sup>
            </li>
          </ul>
        </li>
      </ul>
      <div className="tooltip__heading mb-2">
        Open-label extension<sup>8</sup>
      </div>
      <ul>
        <li>
          From year 1 onwards, patients could have their GC doses adjusted as tolerated to achieve the lowest GC dose that maintained adequate disease control (in the opinion of the investigator)
        </li>
        <li>
          149 patients continued to participate in the open-label extension through year 2
        </li>
      </ul>
    </>;

  useEffect(() => {
    function handleResize() {
      // set for mobile, tablet, desktop
      if (window.innerWidth < 768) {
        setScreenSize('mobile');
      } else if (window.innerWidth < 1039) {
        setScreenSize('tablet');
      } else {
        setScreenSize('desktop');
      }
    }
    handleResize(); // Set initial value
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <section className={styles.container}>
      <div className="container">
        <div id='DoseReduction' className={styles.content} data-question-highlight="none">
          <h1 className={`${styles.mainTitle} page-heading`}>
            {index === 0 && <>
              Both androgen reduction and GC dose reduction were evaluated in CAHtalyst<sup>&reg;</sup> Pediatric<sup>1</sup>
            </>}
            {index === 1 && <>
              Both androgen reduction and GC dose reduction were evaluated in CAHtalyst<sup>&reg;</sup> Adult<sup>1</sup>
            </>}
          </h1>
          
          <p className={styles.subtitle}>
            {index === 0 && <>
              <span className={styles.subtitle_pediatric}>The 28-week, randomized, double-blind, placebo-controlled phase 3 trial addressed questions 1 and 2 below, while the open-label and open-label extension periods addressed question 3<sup className="!font-medium">
1,3,6,9,11</sup>:</span>
            </>}
            {index === 1 && <>
              <span className={styles.subtitle_adult}>The 24-week, randomized, double-blind, placebo-controlled phase 3 trial addressed questions 1 and 2 below, while the open-label and open-label extension periods addressed question 3<sup className="!font-medium">1,7,10</sup>:</span>
            </>}
          </p>

          <div className={index === 0 ? styles.questions : `${styles.questions} ${styles.questions_state2}`}>
            <div className={styles.questions_1}>
              <p>Question 1</p>
              <Button
                customClass={styles.questions_button}
                type="button"
                onClick={()=>questionHighlight('q1')}
                data-gtm-event-category="Action"
                data-gtm-event-action="Click"
                data-gtm-event-label="Could CRENESSITY treatment improve androgen control?"
              >
                Could CRENESSITY treatment improve androgen control?
              </Button>
            </div>
            <div className={styles.questions_2}>
              <p>Question 2</p>
              <Button
                customClass={styles.questions_button}
                type="button"
                onClick={()=>questionHighlight('q2')}
                data-gtm-event-category="Action"
                data-gtm-event-action="Click"
                data-gtm-event-label="Could CRENESSITY treatment enable protocolized GC dose reduction to a physiologic range?"
              >
                {index === 0 && <>
                  Could CRENESSITY treatment enable personalized GC dose reduction to a physiologic range?
                </>}
                {index === 1 && <>
                  Could CRENESSITY treatment enable protocolized GC dose reduction to a physiologic range?
                </>}
              </Button>
            </div>
            <div className={styles.questions_3}>
              <p>Question 3</p>
              <Button
                customClass={styles.questions_button}
                type="button"
                onClick={()=>questionHighlight('q3')}
                data-gtm-event-category="Action"
                data-gtm-event-action="Click"
                data-gtm-event-label=" Does long-term treatment with CRENESSITY improve clinical outcomes?"
              >
                Does long-term treatment with CRENESSITY improve clinical outcomes?
              </Button>
            </div>
          </div>

          <div className={styles.imageWrapper}>
            <div className={styles.explanationBoxes}>
              <div className={`${styles.explanationBox} ${index === 1 ? styles.explanationBox_alt : styles.explanationBox_alt_2}`}>
                <strong>Androstenedione reduction was evaluated<br />
                at week 4 while GCs remained steady.</strong>
              </div>
              <div className={`${styles.explanationBox} ${styles.explanationBox2} ${index === 1 ? styles.explanationBox2_alt : ''}`}>
              <strong>GC dose reduction (while maintaining or improving <br /><Button
                  data-gtm-event-category="Action"
                  data-gtm-event-action="Click"
                  data-gtm-event-label="Androstenedione levels"
                  type="tooltip"
                  tooltipData={({
                    offsetY: screenSize === 'mobile' ? 0 : 200,
                    heading: index === 0
                      ? <>Maintaining or improving androstenedione levels<sup>1,9</sup></>
                      : <>Maintaining or improving androstenedione levels<sup>1,10,25</sup></>,
                    content: <ul>
                      {index === 0 && <li>
                        Defined as &le;120% of the baseline value or &le;ULN for sex and either age (for Tanner stage 1) or pubertal stage (for Tanner stages 2-5)
                      </li>}
                      {index === 1 && <li>
                        Defined as &le;120% of the baseline value or &le;ULN for age and sex
                      </li>}
                    </ul>
                  })}
                  customClass="sup-info text-hcp-eggplant font-bold !underline"
                >androstenedione levels</Button>) was evaluated at week {index === 0 && <>28</>}{index === 1 && <>24</>}.</strong>
              </div>
            </div>
            {index === 0 && <picture>
              <source
                media="(max-width: 767px)"
                width={342}
                height={824}
                srcSet="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Trial-Design-Chart_mobile.svg"
              />
              <CustomImage
                src="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Trial-Design-Chart.svg"
                alt="Study design included a 28-week, randomized, double-blind, placebo-controlled phase 3 trial and open-label phases."
                width={1280}
                height={250}
                // useMap="#imagemap1"
                className={styles.chartImage}
              />
              <Button
                customClass={styles.tooltip_2}
                type="tooltip"
                tooltipData={({
                  maxWidth: '504px',
                  offsetX: screenSize === 'mobile' ? 0 : screenSize === 'tablet' ? -100 : -250,
                  heading: <span className="inline-block">Gradual reduction of GC dose<sup>1,9,11</sup></span>,
                  content: <ul>
                    <li>
                      GC dose reduction was personalized. Doses were reduced <strong>only when androstenedione levels 
                      were maintained or improved</strong>, which was defined as &le;120% of baseline or &le;ULN for sex 
                      and either age (for Tanner stage 1) or pubertal stage (for Tanner stages 2-5) 
                    </li>
                    <li>
                      GC doses were adjusted in 1 to 4 steps to reach a more physiologic range (8-10 mg/m<sup>2</sup>/day)
                    </li>
                    <li>Patients who were unable to maintain or improve androstenedione levels with a GC dose reduction were considered to have a 0% change in daily GC dose</li>
                    <li>
                      A safety follow-up was conducted by phone after each GC dose reduction
                    </li>
                  </ul>
                })}
                data-gtm-event-category="Action"
                data-gtm-event-action="Click"
                data-gtm-event-label="Gradual reduction of GC dose"
              />
              <Button
                customClass={styles.tooltip_2_mobile}
                type="tooltip"
                tooltipData={({
                  heading: <>Maintaining or improving androstenedione levels<sup>1,5</sup></>,
                  content: <ul>
                    <li>
                      Defined as &le;120% of the baseline value or &le;ULN for sex and either age (for Tanner stage 1) or pubertal stage (for Tanner stages 2-5)
                    </li>
                  </ul>
                })}
                data-gtm-event-category="Action"
                data-gtm-event-action="Click"
                data-gtm-event-label="Maintaining or improving androstenedione levels"
              />
              <Button
                type="tooltip"
                tooltipData={({
                  heading: <>Open-label period (months 7-12)<sup>12</sup></>,
                  content: openLabelTreamentTooltipData
                })}
                customClass={styles.tooltip_3_mobile}
              />
            </picture>}
            {index === 1 && <picture>
              <source
                width={342}
                height={913}
                media="(max-width: 767px)"
                srcSet="/images/page/hcp/clinical-information/Clinical-Information_Adult-Trial-Design-Chart_mobile.svg"
              />
              <CustomImage
                src="/images/page/hcp/clinical-information/Clinical-Information_Adult-Trial-Design-Chart.svg"
                alt="Study design included a 24-week, randomized, double-blind, placebo-controlled phase 3 trial and open-label phases."
                width={1280}
                height={250}
                className={`${styles.chartImage} ${styles.chartImageState1}`}
              />
              <Button
                customClass={styles.tooltip_2_2}
                type="tooltip"
                tooltipData={({
                  maxWidth: '504px',
                  // offsetX: screenSize === 'mobile' ? 0 : screenSize === 'tablet' ? 100 : 400,
                  heading: <>Reduction of GC doses<sup className="!font-normal">1,8,10,25</sup></>,
                  content: <>
                    <ul className="mb-4">
                      <li>
                        From weeks 4 to 12, all patients had their GC dose reduced as part of the study protocol
                      </li>
                      <li>
                        Doses were reduced in 1 to 4 steps to reach a more physiologic range (15-18 mg/day in hydrocortisone equivalents)<sup>c</sup>
                      </li>
                      <li>
                        A safety follow-up was conducted by phone after each GC dose reduction
                      </li>
                    </ul>
                    <ul className="footnotes">
                      <li>
                        <sup className="hang">c</sup>
                        <span>For ease of reference, the protocol-defined range of <span className="text-nowrap">8-10 mg/m<sup>2</sup>/day</span> was adjusted based on mean baseline body surface area in the study, which was 1.84 m<sup>2</sup>.<sup>8,10</sup></span>
                      </li>
                    </ul>
                  </>
                })}
                data-gtm-event-category="Action"
                data-gtm-event-action="Click"
                data-gtm-event-label="Reduction of GC doses"
              />
              <Button
                customClass={styles.tooltip_2_3}
                type="tooltip"
                tooltipData={({
                  maxWidth: '504px',
                  offsetX: screenSize === 'desktop' ? -350 : 0,
                  heading: <>GC dose optimization<sup>1,10,25</sup></>,
                  content: <ul>
                    <li>
                      From weeks 12 to 24, GC doses were optimized (adjusted up or down) with the goal 
                      of achieving the lowest tolerated dose while maintaining or improving androstenedione 
                      levels, which was defined as ≤120% of baseline value or ≤ULN 
                    </li>
                    <li>If a patient’s androstenedione level was not maintained or improved with GC dose reduction, the % change from baseline for daily GC dose was recorded as 0</li>
                    <li>
                      A safety follow-up was conducted by phone after each GC dose reduction
                    </li>
                  </ul>
                })}
                data-gtm-event-category="Action"
                data-gtm-event-action="Click"
                data-gtm-event-label="GC dose optimization"
              />
              <Button
                type="tooltip"
                tooltipData={({
                  heading: <>Maintaining or improving androstenedione levels<sup>1,10,25</sup></>,
                  content: <ul>
                    <li>
                      Defined as &le;120% of the baseline value or &le;ULN for age and sex
                    </li>
                  </ul>
                })}
                customClass={styles.tooltip_2_4}
                data-gtm-event-category="Action"
                data-gtm-event-action="Click"
                data-gtm-event-label="Maintaining or improving androstenedione levels"
              />
              <Button
                type="tooltip"
                tooltipData={({
                  maxWidth: '550px',
                  heading: <>Open-label period (months 6-12)<sup>26</sup></>,
                  content: openLabelTreamentTooltipData,
                })}
                customClass={styles.tooltip_2_5}
                data-gtm-event-category="Action"
                data-gtm-event-action="Click"
                data-gtm-event-label="28-week open-label treatment"
              />
            </picture>}
            <div className={styles.imageWrapperBottom}>
              <span>2:1 randomization</span>
              <div className={styles.imageWrapperBottomLine}>
                <div className={styles.imageWrapperBottomLineTop}>
                 {index === 0 && <>
                  <span>Open-label period <br />(months 7-12)</span>
                 </>}
                 {index === 1 && <>
                  <span>Open-label period <br />(months 6-12)</span>
                 </>}
                <span>Open-label <br />extension (ongoing)</span>
                </div>
                <Button
                  type="tooltip"
                  tooltipData={({
                    maxWidth: '550px',
                    offsetX: screenSize === 'mobile' ? 0 : screenSize === 'tablet' ? -100 : index === 0 ? -220 : -400,
                    offsetY: screenSize === 'mobile' ? 0 : index === 0 ? 0 : -100,
                    heading: index === 0
                      ? <>Open-label period (months 7-12)<sup>12</sup></>
                      : <>Open-label period (months 6-12)<sup>26</sup></>,
                    content: openLabelTreamentTooltipData
                  })}
                  customClass="sup-info text-hcp-eggplant "
                  data-gtm-event-category="Action"
                  data-gtm-event-action="Click"
                  data-gtm-event-label={index === 0 ? '28-week open-label treatment' : '24-week open-label treatment'}
                >
                  Open-label treatment
                </Button>
              </div>
            </div>
          </div>

          <ul className="footnotes">
            <li>
              <sup className="hang">a</sup>
              <span>
                Androstenedione {index===0? 'and 17-OHP were' : 'was'} measured as change from baseline. GC dose was measured as % change from baseline.
                <sup>1</sup>
              </span>
            </li>
            <li>
              <sup className="hang">b</sup>
              <span>
                
                {index === 0 && <>Doses were adjusted to maintain or improve androstenedione levels at ≤120% of the baseline value or ≤ULN. For patients who were unable to maintain or improve androstenedione levels, the % change from baseline for daily GC dose was recorded as 0.<sup>1,9</sup></>}
                {index === 1 && <>Doses were adjusted while maintaining or improving androstenedione levels at ≤120% of the baseline value or ≤ULN. For patients who were unable to maintain or improve baseline androstenedione levels, the % change from baseline for daily GC dose was recorded as 0.<sup>1,10</sup></>}
              </span>
            </li>
            {index === 1 && <li>
              <sup className="hang">c</sup>
              <span>For ease of reference, the protocol-defined range of 8-10 mg/m<sup>2</sup>/day was adjusted based on mean baseline body surface area in the study, which was 1.84 m<sup>2</sup>.</span>
            </li>}
          </ul>
        </div>
      </div>
    </section>
  );
}
